import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type Color } from '@air/primitive-color-picker';

import { useLibraries } from '~/components/LibraryBeta/hooks/queries/useLibraries';
import { useGeneralLibraryMetadata } from '~/components/LibraryBeta/hooks/useGeneralLibraryMetadata';
import {
  flattenWorkspaceAgentBoardTree,
  toWorkspaceAgentBoardTreeBrowseRoots,
  toWorkspaceAgentBoardTreeOpenBoardIds,
  WORKSPACE_AGENT_EMPTY_BOARD_TREE_BOARDS,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/flattenWorkspaceAgentBoardTree';
import { groupWorkspaceAgentBoardSearchMatches } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/groupWorkspaceAgentBoardSearchMatches';
import { type WorkspaceAgentBoardMentionSource } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/toWorkspaceAgentBoardMentionItem';
import {
  type WorkspaceAgentBoardTreeSearchResult,
  type WorkspaceAgentLibraryMentionSource,
  type WorkspaceAgentMentionSearchParams,
  type WorkspaceAgentMentionTreeItem,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { useWorkspaceAgentBoardTreeExpansion } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/useWorkspaceAgentBoardTreeExpansion';
import {
  isWorkspaceAgentBoardListReadyForNextPage,
  toWorkspaceAgentBoardListQuerySpec,
  type WorkspaceAgentBoardListQuerySpec,
  type WorkspaceAgentBoardListSnapshot,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentBoardListQuery';
import { getWorkspaceAgentMentionMatchStrength } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionItems';
import { usePrivateWorkspaceAgentBoardMentionSearch } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentBoardMentionSearch';
import { GeneralLibrary } from '~/utils/librariesUtils';

const EMPTY_LIBRARIES: WorkspaceAgentLibraryMentionSource[] = [];

const EMPTY_BOARD_LIST_SNAPSHOTS: Record<string, WorkspaceAgentBoardListSnapshot> = {};

const EMPTY_OPEN_BOARD_IDS: string[] = [];

const EMPTY_NEXT_PAGE_NODE_IDS: ReadonlySet<string> = new Set();

const EMPTY_FETCHED_LIBRARY_IDS: ReadonlySet<string> = new Set();

const EMPTY_PENDING_NODE_IDS: ReadonlySet<string> = new Set();

const EMPTY_BOARD_LIST_QUERY_SPECS: WorkspaceAgentBoardListQuerySpec[] = [];

const EMPTY_BROWSE_ITEMS: WorkspaceAgentMentionTreeItem[] = [];

const noopWorkspaceAgentBoardListFetch = () => undefined;

/**
 * `useGeneralLibraryMetadata` returns the picker `Color` (`hex`). Flatten reads `Library.color.backgroundHex`.
 * Font is unused on the row; Grey's `#333333` matches the metadata fallback.
 */
const WORKSPACE_AGENT_GENERAL_LIBRARY_FONT_HEX = '#333333';

const toWorkspaceAgentGeneralLibrarySource = (
  title: string,
  icon: string,
  color: Color,
): WorkspaceAgentLibraryMentionSource => ({
  color: {
    backgroundHex: color.hex,
    fontHex: WORKSPACE_AGENT_GENERAL_LIBRARY_FONT_HEX,
    id: color.id,
    name: color.name,
  },
  icon,
  id: GeneralLibrary.id,
  title,
});

export type UsePrivateWorkspaceAgentBoardTreeParams = WorkspaceAgentMentionSearchParams & {
  /** Passed through to expansion. Omitted unless the tree should open on a known path. */
  initialExpandedNodeIds?: readonly string[];
  /**
   * Whether the picker itself is open, which is not `enabled`: `enabled` is this one source's, and
   * drops whenever Boards is not the active chip. What the user opened in the tree belongs to the
   * picker session, so only the session ending may take it away — a trip to the Assets chip and
   * back must find the same boards open.
   */
  isPickerOpen: boolean;
};

/**
 * Board rows as a tree flattened into one list (the popup navigates by index). With named
 * libraries it mirrors the sidebar: a Libraries group, Global first when it has roots and open by
 * default, then named libraries closed until opened. With none, general roots sit at depth 0 — no
 * Libraries heading, no Global row. Never send `'general'` as `libraryIds`.
 *
 * Each expanded node has its own infinite `Boards.list` — Global (`libraryBoards: 'NO'`), a named
 * library (`libraryIds: [id]`), a board (`view: boardId`). A collapsed row the highlight rests on
 * prefetches that same first page into the query cache; flatten ignores it until the row is open, or
 * hover would rebuild the list under the pointer. Next pages load when the last child of an *open*
 * node nears the fold, not from a workspace-wide mixed list.
 *
 * With a query the same flatten places each match under its ancestors and library; nothing is
 * fetched under those rows, so a chevron drawn from a server count would open onto nothing.
 *
 * Expansion is stored as overrides over each node's default, not a snapshot of it.
 */
export const usePrivateWorkspaceAgentBoardTree = ({
  enabled,
  initialExpandedNodeIds,
  isPickerOpen,
  query,
  workspaceId,
}: UsePrivateWorkspaceAgentBoardTreeParams): WorkspaceAgentBoardTreeSearchResult => {
  const hasQuery = query !== '';
  const { expansionOverrides, isExpanded, isSearchExpanded, toggleExpanded } = useWorkspaceAgentBoardTreeExpansion({
    initialExpandedNodeIds,
    isPickerOpen,
    query,
  });
  const search = usePrivateWorkspaceAgentBoardMentionSearch({ enabled: enabled && hasQuery, query, workspaceId });
  const [listSnapshots, setListSnapshots] =
    useState<Record<string, WorkspaceAgentBoardListSnapshot>>(EMPTY_BOARD_LIST_SNAPSHOTS);
  const listFetchersRef = useRef<Record<string, Pick<WorkspaceAgentBoardListSnapshot, 'fetchNextPage' | 'refetch'>>>(
    {},
  );
  /** Latest observer payload per node, including prefetch. Flatten state only tracks expanded nodes. */
  const allListSnapshotsRef = useRef<Record<string, WorkspaceAgentBoardListSnapshot>>({});
  const expandedNodeIdsRef = useRef<ReadonlySet<string>>(EMPTY_FETCHED_LIBRARY_IDS);

  const {
    data: librariesData,
    isError: isLibrariesError,
    isFetching: isFetchingLibraries,
    isLoading: isLoadingLibraries,
    refetch: refetchLibraries,
  } = useLibraries({ enabled });

  const { generalLibraryColor, generalLibraryIcon, generalLibraryTitle } = useGeneralLibraryMetadata();

  const libraries = useMemo<WorkspaceAgentLibraryMentionSource[]>(
    () => librariesData?.pages.flatMap((page) => page.data) ?? EMPTY_LIBRARIES,
    [librariesData],
  );

  const generalLibrary = useMemo(
    () => toWorkspaceAgentGeneralLibrarySource(generalLibraryTitle, generalLibraryIcon, generalLibraryColor),
    [generalLibraryColor, generalLibraryIcon, generalLibraryTitle],
  );

  const libraryIds = useMemo(
    () => new Set([GeneralLibrary.id, ...libraries.map((library) => library.id)]),
    [libraries],
  );

  const openBoardIds = useMemo(
    () => (hasQuery ? EMPTY_OPEN_BOARD_IDS : toWorkspaceAgentBoardTreeOpenBoardIds(expansionOverrides, libraryIds)),
    [expansionOverrides, hasQuery, libraryIds],
  );

  const listQuerySpecs = useMemo<WorkspaceAgentBoardListQuerySpec[]>(() => {
    if (!enabled || !workspaceId) {
      return EMPTY_BOARD_LIST_QUERY_SPECS;
    }

    // A library matched by name opens onto its roots; board children are never fetched under a query.
    // Wait until search has looked, and skip a library that already has hits — otherwise roots
    // flash in on the live keystroke and get replaced when the debounced matches land.
    if (hasQuery) {
      if (!search.hasLooked) {
        return EMPTY_BOARD_LIST_QUERY_SPECS;
      }

      const normalizedQuery = search.debouncedQuery.trim().toLowerCase();

      // `hasQuery` is the raw string, so whitespace-only is still search mode; `includes('')` would
      // otherwise queue a roots spec for every named library.
      if (normalizedQuery === '') {
        return EMPTY_BOARD_LIST_QUERY_SPECS;
      }

      const specs: WorkspaceAgentBoardListQuerySpec[] = [];
      const matchedLibraryIds = new Set(
        search.items.flatMap((item) => (item.kind === 'board' && item.library ? [item.library.id] : [])),
      );
      const hasGeneralMatches = search.items.some((item) => item.kind === 'board' && !item.library);

      if (!hasGeneralMatches && generalLibrary.title.toLowerCase().includes(normalizedQuery)) {
        specs.push(toWorkspaceAgentBoardListQuerySpec({ id: GeneralLibrary.id, kind: 'library' }, workspaceId));
      }

      for (const library of libraries) {
        if (library.title.toLowerCase().includes(normalizedQuery) && !matchedLibraryIds.has(library.id)) {
          specs.push(toWorkspaceAgentBoardListQuerySpec({ id: library.id, kind: 'library' }, workspaceId));
        }
      }

      return specs;
    }

    const specs: WorkspaceAgentBoardListQuerySpec[] = [];

    if (isExpanded(GeneralLibrary.id, true)) {
      specs.push(toWorkspaceAgentBoardListQuerySpec({ id: GeneralLibrary.id, kind: 'library' }, workspaceId));
    }

    for (const library of libraries) {
      if (isExpanded(library.id, false)) {
        specs.push(toWorkspaceAgentBoardListQuerySpec({ id: library.id, kind: 'library' }, workspaceId));
      }
    }

    for (const boardId of openBoardIds) {
      specs.push(toWorkspaceAgentBoardListQuerySpec({ id: boardId, kind: 'board' }, workspaceId));
    }

    return specs;
  }, [
    enabled,
    generalLibrary.title,
    hasQuery,
    isExpanded,
    libraries,
    openBoardIds,
    search.debouncedQuery,
    search.hasLooked,
    search.items,
    workspaceId,
  ]);

  expandedNodeIdsRef.current = new Set(listQuerySpecs.map((spec) => spec.nodeId));

  const onListSnapshot = useCallback((nodeId: string, snapshot: WorkspaceAgentBoardListSnapshot) => {
    listFetchersRef.current[nodeId] = { fetchNextPage: snapshot.fetchNextPage, refetch: snapshot.refetch };
    allListSnapshotsRef.current[nodeId] = snapshot;

    // Prefetch must not rebuild the visible rows: that retargets the highlight and scrollIntoView
    // fights the pointer. The query cache is warm; expand copies this snapshot into flatten state.
    if (!expandedNodeIdsRef.current.has(nodeId)) {
      return;
    }

    setListSnapshots((current) => {
      const previous = current[nodeId];

      if (
        previous &&
        previous.boards === snapshot.boards &&
        previous.hasNextPage === snapshot.hasNextPage &&
        previous.isError === snapshot.isError &&
        previous.isFetched === snapshot.isFetched &&
        previous.isFetching === snapshot.isFetching &&
        previous.isFetchNextPageError === snapshot.isFetchNextPageError &&
        previous.isFetchingNextPage === snapshot.isFetchingNextPage
      ) {
        return current;
      }

      return { ...current, [nodeId]: snapshot };
    });
  }, []);

  useEffect(() => {
    setListSnapshots((current) => {
      let next = current;

      for (const spec of listQuerySpecs) {
        const snapshot = allListSnapshotsRef.current[spec.nodeId];

        if (!snapshot || current[spec.nodeId] === snapshot) {
          continue;
        }

        if (next === current) {
          next = { ...current };
        }

        next[spec.nodeId] = snapshot;
      }

      return next;
    });
  }, [listQuerySpecs]);

  const wasPickerOpenRef = useRef(isPickerOpen);

  useEffect(() => {
    const wasPickerOpen = wasPickerOpenRef.current;

    wasPickerOpenRef.current = isPickerOpen;

    if (wasPickerOpen && !isPickerOpen) {
      allListSnapshotsRef.current = {};
      listFetchersRef.current = {};
      setListSnapshots(EMPTY_BOARD_LIST_SNAPSHOTS);
    }
  }, [isPickerOpen]);

  const generalSnapshot = listSnapshots[GeneralLibrary.id];
  const generalRootBoards = generalSnapshot?.boards ?? WORKSPACE_AGENT_EMPTY_BOARD_TREE_BOARDS;

  const rootBoardsByLibraryId = useMemo(() => {
    const boardsByLibraryId: Record<string, WorkspaceAgentBoardMentionSource[]> = {};

    for (const library of libraries) {
      const snapshot = listSnapshots[library.id];

      if (snapshot) {
        boardsByLibraryId[library.id] = [...snapshot.boards];
      }
    }

    return boardsByLibraryId;
  }, [libraries, listSnapshots]);

  const boardsByNodeId = useMemo(() => {
    const boardsById: Record<string, WorkspaceAgentBoardMentionSource[]> = {};

    for (const boardId of openBoardIds) {
      const snapshot = listSnapshots[boardId];

      if (snapshot) {
        boardsById[boardId] = [...snapshot.boards];
      }
    }

    return boardsById;
  }, [listSnapshots, openBoardIds]);

  const fetchedLibraryIds = useMemo(() => {
    const fetchedIds = new Set<string>();

    for (const [nodeId, snapshot] of Object.entries(listSnapshots)) {
      if (snapshot.isFetched) {
        fetchedIds.add(nodeId);
      }
    }

    return fetchedIds.size > 0 ? fetchedIds : EMPTY_FETCHED_LIBRARY_IDS;
  }, [listSnapshots]);

  const hasNextPageNodeIds = useMemo(() => {
    const nodeIds = new Set<string>();

    for (const spec of listQuerySpecs) {
      const snapshot = listSnapshots[spec.nodeId];

      if (isWorkspaceAgentBoardListReadyForNextPage(snapshot)) {
        nodeIds.add(spec.nodeId);
      }
    }

    return nodeIds.size > 0 ? nodeIds : EMPTY_NEXT_PAGE_NODE_IDS;
  }, [listQuerySpecs, listSnapshots]);

  const pendingChildCountNodeIds = useMemo(() => {
    const nodeIds = new Set<string>();

    for (const spec of listQuerySpecs) {
      const snapshot = listSnapshots[spec.nodeId];

      if (
        !snapshot ||
        snapshot.isError ||
        (snapshot.isFetching && !snapshot.isFetchingNextPage && !snapshot.isFetched)
      ) {
        nodeIds.add(spec.nodeId);
      }
    }

    return nodeIds.size > 0 ? nodeIds : EMPTY_PENDING_NODE_IDS;
  }, [listQuerySpecs, listSnapshots]);

  const browseItems = useMemo(() => {
    // `libraries` is `[]` while `Libraries.list` is in flight — flattening that as "no named
    // libraries" would paint general roots at depth 0, then rebuild once headings land.
    if (isLoadingLibraries) {
      return EMPTY_BROWSE_ITEMS;
    }

    const roots = toWorkspaceAgentBoardTreeBrowseRoots({
      boardsByNodeId,
      fetchedLibraryIds,
      generalLibrary,
      generalRootBoards,
      hasNextPageNodeIds,
      headingLibraries: libraries,
      rootBoardsByLibraryId,
    });

    return flattenWorkspaceAgentBoardTree(
      roots,
      isExpanded,
      libraries.length === 0 && hasNextPageNodeIds.has(GeneralLibrary.id),
      (nodeId) => hasNextPageNodeIds.has(nodeId),
    );
  }, [
    boardsByNodeId,
    fetchedLibraryIds,
    generalLibrary,
    generalRootBoards,
    hasNextPageNodeIds,
    isExpanded,
    isLoadingLibraries,
    libraries,
    rootBoardsByLibraryId,
  ]);

  const searchItems = useMemo(() => {
    // Same reason as `browseItems`: `libraries` is `[]` in flight, and grouping branches on
    // `libraries.length`, so an ungated search paints hits at depth 0 and rebuilds headings.
    if (isLoadingLibraries) {
      return EMPTY_BROWSE_ITEMS;
    }

    return flattenWorkspaceAgentBoardTree(
      groupWorkspaceAgentBoardSearchMatches({
        generalLibrary,
        generalRootBoards,
        libraries,
        matches: search.items,
        query: search.debouncedQuery,
        rootBoardsByLibraryId,
      }),
      isSearchExpanded,
    );
  }, [
    generalLibrary,
    generalRootBoards,
    isLoadingLibraries,
    isSearchExpanded,
    libraries,
    rootBoardsByLibraryId,
    search.debouncedQuery,
    search.items,
  ]);

  const fetchNextPageForNode = useCallback((nodeId: string) => {
    listFetchersRef.current[nodeId]?.fetchNextPage();
  }, []);

  const fetchNextPage = useCallback(noopWorkspaceAgentBoardListFetch, []);

  const refetch = useCallback(() => {
    refetchLibraries();

    for (const spec of listQuerySpecs) {
      listFetchersRef.current[spec.nodeId]?.refetch();
    }
  }, [listQuerySpecs, refetchLibraries]);

  const { refetch: refetchSearch } = search;

  const refetchSearchTree = useCallback(() => {
    refetchSearch();
    refetch();
  }, [refetch, refetchSearch]);

  const isTreeError = isLibrariesError || listQuerySpecs.some((spec) => listSnapshots[spec.nodeId]?.isError);
  const isTreeFetching = isFetchingLibraries || listQuerySpecs.some((spec) => listSnapshots[spec.nodeId]?.isFetching);
  const isGeneralFetched = !!generalSnapshot?.isFetched || !isExpanded(GeneralLibrary.id, true);
  const hasTreeLooked = isGeneralFetched && !isLoadingLibraries;
  const isTreeLoading = enabled && (isLoadingLibraries || (!isGeneralFetched && !generalSnapshot?.isError));

  return useMemo(() => {
    if (hasQuery) {
      return {
        fetchNextPage: search.fetchNextPage,
        fetchNextPageForNode,
        hasLooked: search.hasLooked && !isLoadingLibraries,
        hasNextPage: search.hasNextPage,
        hasNextPageNodeIds: EMPTY_NEXT_PAGE_NODE_IDS,
        isError: search.isError || isLibrariesError,
        isFetching: search.isFetching || isTreeFetching,
        isLoading: search.isLoading,
        isStale: search.isStale,
        items: searchItems,
        matchStrength: getWorkspaceAgentMentionMatchStrength(searchItems, query),
        listQuerySpecs,
        onListSnapshot,
        pendingChildCountNodeIds: EMPTY_PENDING_NODE_IDS,
        refetch: refetchSearchTree,
        toggleExpanded,
      };
    }

    return {
      fetchNextPage,
      fetchNextPageForNode,
      hasLooked: hasTreeLooked,
      hasNextPage: false,
      hasNextPageNodeIds,
      isError: isTreeError,
      isFetching: isTreeFetching,
      isLoading: isTreeLoading,
      isStale: false,
      items: browseItems,
      matchStrength: 'none',
      listQuerySpecs,
      onListSnapshot,
      pendingChildCountNodeIds,
      refetch,
      toggleExpanded,
    };
  }, [
    browseItems,
    fetchNextPage,
    fetchNextPageForNode,
    hasNextPageNodeIds,
    hasQuery,
    hasTreeLooked,
    isLibrariesError,
    isLoadingLibraries,
    isTreeError,
    isTreeFetching,
    isTreeLoading,
    listQuerySpecs,
    onListSnapshot,
    pendingChildCountNodeIds,
    query,
    refetch,
    refetchSearchTree,
    search.fetchNextPage,
    search.hasLooked,
    search.hasNextPage,
    search.isError,
    search.isFetching,
    search.isLoading,
    search.isStale,
    searchItems,
    toggleExpanded,
  ]);
};
